import type { AiManagedGroupEligibleCampaign } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { CampaignStatusBadge } from "@/components/campaign/CampaignStatusBadge";
import {
  type CandidateSortDirection,
  type CandidateSortField,
  ManagedGroupCandidateSortHeader,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { DataTableFooter } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

interface CampaignTransferPaneProps {
  kind: "available" | "selected";
  title: string;
  countLabel: string;
  rows: AiManagedGroupEligibleCampaign[];
  checkedIds: Set<string>;
  allRowsChecked: boolean;
  someRowsChecked: boolean;
  isError: boolean;
  isLoading: boolean;
  emptyText: string;
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  sortBy: CandidateSortField;
  sortDirection: CandidateSortDirection;
  checkboxLabel: (campaign: AiManagedGroupEligibleCampaign) => string;
  selectAllLabel: string;
  onPageChange: (page: number) => void;
  onToggleAll: (checked: boolean) => void;
  onToggle: (campaign: AiManagedGroupEligibleCampaign) => void;
  onSortChange: (field: CandidateSortField) => void;
}

export function CampaignTransferPane({
  kind,
  title,
  countLabel,
  rows,
  checkedIds,
  allRowsChecked,
  someRowsChecked,
  isError,
  isLoading,
  emptyText,
  page,
  pageCount,
  pageSize,
  total,
  sortBy,
  sortDirection,
  checkboxLabel,
  selectAllLabel,
  onPageChange,
  onToggleAll,
  onToggle,
  onSortChange,
}: CampaignTransferPaneProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div
      data-slot="campaign-transfer-pane"
      data-pane={kind}
      className={cn(
        "flex min-w-0 flex-1 flex-col overflow-hidden first:rounded-l-[9px] last:rounded-r-[9px]",
        kind === "available" && "border-r border-line",
      )}
    >
      <div className="flex h-[45px] shrink-0 items-center justify-between gap-2 border-b border-line px-2.5">
        <div className="flex min-w-0 flex-col gap-px">
          <p className="truncate text-[11px] leading-[14px] font-semibold">{title}</p>
          <p className="truncate text-[9px] leading-3 text-muted-foreground">{countLabel}</p>
        </div>
        <Badge size="compact" variant="neutral" className="font-normal">
          {t("create.selectedCapacity", { count: total })}
        </Badge>
      </div>
      <div className="flex h-6 shrink-0 items-center gap-2.5 border-b border-line bg-subtle px-2.5 text-[8px] leading-3 font-medium tracking-[0.04em] text-muted-foreground uppercase">
        <span className="w-4 shrink-0">
          <Checkbox
            className="size-4"
            aria-label={selectAllLabel}
            checked={allRowsChecked}
            indeterminate={!allRowsChecked && someRowsChecked}
            disabled={rows.length === 0}
            onCheckedChange={(checked) => onToggleAll(checked === true)}
          />
        </span>
        <span className="min-w-0 flex-1">{t("create.columns.campaignPortfolio")}</span>
        <ManagedGroupCandidateSortHeader
          className="w-[64px]"
          field="aiStatus"
          label={t("create.columns.aiStatus")}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <ManagedGroupCandidateSortHeader
          className="w-[76px]"
          field="delivery"
          label={t("create.columns.delivery")}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="p-5 text-sm text-destructive-foreground">{t("loadFailed")}</p>
        ) : rows.length ? (
          rows.map((campaign) => (
            <div
              key={campaign.id}
              className="flex h-12 items-center gap-2.5 border-b border-line px-2.5 transition-colors hover:bg-subtle/60"
            >
              <span className="w-4 shrink-0">
                <Checkbox
                  className="size-4"
                  aria-label={checkboxLabel(campaign)}
                  checked={checkedIds.has(campaign.id)}
                  disabled={campaign.status === "archived"}
                  onCheckedChange={() => onToggle(campaign)}
                />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-px pr-2">
                <span className="truncate text-[11px] leading-[14px] font-semibold">
                  {campaign.name}
                </span>
                <span className="truncate text-[9px] leading-3 text-muted-foreground">
                  {campaign.portfolio?.name ?? t("candidateFilters.noPortfolio")} ·{" "}
                  {campaign.targetingType
                    ? t(`create.targeting.${campaign.targetingType}`)
                    : t("candidateFilters.unknownTargeting")}
                </span>
              </span>
              <span className="w-[64px] shrink-0">
                <Badge
                  size="compact"
                  variant={campaign.aiManaged ? "success" : "neutral"}
                  className="font-normal"
                >
                  {campaign.aiManaged ? t("create.aiOnLabel") : t("create.aiOffLabel")}
                </Badge>
              </span>
              <span className="w-[76px] shrink-0">
                <CampaignStatusBadge status={campaign.status} />
              </span>
            </div>
          ))
        ) : (
          <div className="flex h-full items-center justify-center px-5 text-center text-xs text-muted-foreground">
            {emptyText}
          </div>
        )}
      </div>
      <DataTableFooter className="h-[38px] px-2.5">
        <DataTablePagination
          density="compact"
          page={page}
          pageCount={pageCount}
          summary={t("create.pagination", { from, to, total })}
          onPageChange={onPageChange}
        />
      </DataTableFooter>
    </div>
  );
}
